import { USER_ROLES } from "@/constants";
import { PHISH_SERVICE, SEG_SERVICE } from "@/utils/services";
import { useFlag } from "@/composables/useFlags/useFlag";

const { isFeatureAvailable: isExemptionsAvailable } = useFlag("EXEMPTIONS");

const children = [
  {
    path: "overview",
    name: "customer-email_security-overview",
    component: () =>
      import("@/modules/PhishTitan/views/Customer/Overview/index.vue"),
    exact: true,
    meta: {
      authorize: [USER_ROLES.SYSTEM, USER_ROLES.SALES, USER_ROLES.MSP],
    },
  },
  {
    path: "reports",
    name: "customer-email_security-reports",
    exact: true,
    component: () =>
      import("@/modules/PhishTitan/views/Customer/Reports/index.vue"),
    redirect: {
      name: "insightsCustomerRoute",
    },
    children: [
      {
        path: "",
        name: "insightsCustomerRoute",
        component: () =>
          import(
            "@/modules/PhishTitan/views/Customer/Reports/PowerBi/index.vue"
          ),
        redirect: {
          name: "summaryCustomerReport",
        },
        children: [
          {
            path: "summary",
            name: "summaryCustomerReport",
            component: () =>
              import(
                "@/modules/PhishTitan/views/Customer/Reports/PowerBi/Summary/index.vue"
              ),
          },
          {
            path: "insights",
            name: "insightsCustomerReport",
            component: () =>
              import(
                "@/modules/PhishTitan/views/Customer/Reports/PowerBi/ThreatSurfaceReport/index.vue"
              ),
          },
        ],
      },
    ],
  },
  {
    path: "notifications",
    name: "customer-email_security-notifications",
    component: () =>
      import("@/modules/PhishTitan/views/Customer/Notifications/index.vue"),
  },
  {
    path: "configuration",
    name: "customer-email_security-configuration",
    exact: true,
    redirect: {
      name: "customer-configuration-mail-flow",
    },
    children: [
      {
        path: "mail_flow",
        name: "customer-configuration-mail-flow",
        component: () =>
          import(
            "@/modules/PhishTitan/views/Customer/Configuration/MailFlow/MailFlow.vue"
          ),
        exact: true,
        redirect: {
          name: "customer-configuration-mail-flow-remediation",
        },
        children: [
          {
            path: "allow_list",
            name: "customer-configuration-mail-flow-allow-list",
            component: () =>
              import(
                "@/modules/PhishTitan/views/Customer/Configuration/AllowList/index.vue"
              ),
            beforeEnter: (_to, _from, next) => {
              if (isExemptionsAvailable.value) {
                next({ name: "customer-configuration-mail-flow" });
                return;
              }
              next();
            },
          },
          {
            path: "remediation",
            name: "customer-configuration-mail-flow-remediation",
            component: () =>
              import("@/modules/PhishTitan/views/Common/Remediation/index.vue"),
          },
          {
            path: "exploited_domains",
            name: "customer-configuration-mail-flow-exploited-domains",
            component: () =>
              import(
                "@/modules/PhishTitan/views/Common/ExploitedDomains/index.vue"
              ),
          },
          {
            path: "anti_spoofing",
            name: "customer-configuration-mail-flow-anti-spoofing",
            component: () =>
              import(
                "@/modules/PhishTitan/views/Common/AntiSpoofing/AntiSpoofing.vue"
              ),
          },
          {
            path: "graymail",
            name: "customer-configuration-mail-flow-graymail",
            component: () =>
              import("@/modules/PhishTitan/views/Common/Graymail/Graymail.vue"),
          },
          {
            path: "frontline",
            name: "customer-configuration-mail-flow-frontline",
            meta: {
              solutionId: SEG_SERVICE,
            },
            component: () =>
              import(
                "@/modules/PhishTitan/views/Common/Frontline/Frontline.vue"
              ),
          },
          {
            path: "geoblocking",
            name: "customer-configuration-mail-flow-geoblocking",
            meta: {
              solutionId: SEG_SERVICE,
            },
            component: () =>
              import(
                "@/modules/PhishTitan/views/Common/Geoblocking/Geoblocking.vue"
              ),
          },
        ],
      },
      {
        path: "link_lock",
        name: "customer-email_security-config-link-lock",
        exact: true,
        component: () =>
          import(
            "@/modules/PhishTitan/views/Customer/Configuration/LinkLock/index.vue"
          ),
      },
      {
        path: "exemptions",
        name: "customer-email_security-config-exemptions",
        exact: true,
        component: () =>
          import("@/modules/PhishTitan/views/Common/Exemptions/Exemptions.vue"),
        beforeEnter: (_to, _from, next) => {
          if (!isExemptionsAvailable.value) {
            next({ name: "customer-configuration-mail-flow" });
            return;
          }
          next();
        },
      },
      {
        path: "block_list",
        name: "customer-email_security-config-block-list",
        exact: true,
        component: () =>
          import("@/modules/PhishTitan/views/Common/BlockList/BlockList.vue"),
        meta: {
          solutionId: SEG_SERVICE,
        },
      },
    ],
  },
  {
    path: "incidents",
    name: "customer-email_security-incidents",
    component: () =>
      import(
        "@/modules/PhishTitan/views/Customer/Incidents/Overview/index.vue"
      ),
  },
  {
    path: "incidents/incident_summary",
    name: "customer-email_security-incidents-incident-summary",
    component: () =>
      import(
        "@/modules/PhishTitan/views/Customer/Incidents/IncidentSummary/IncidentSummary.vue"
      ),
    redirect: {
      name: "customer-email_security-incidents-incident-summary-details",
    },
    children: [
      {
        path: "details",
        name: "customer-email_security-incidents-incident-summary-details",
        component: () =>
          import(
            "@/modules/PhishTitan/views/Customer/Incidents/IncidentSummary/Details/index.vue"
          ),
      },
      {
        path: "threat-coach",
        name: "customer-email_security-incidents-incident-summary-threat-coach",
        component: () =>
          import(
            "@/modules/PhishTitan/views/Common/Incidents/IncidentSummary/ThreatCoach/ThreatCoach"
          ),
      },
      {
        path: "affected-users",
        name: "customer-email_security-incidents-incident-summary-affected-users",
        component: () =>
          import(
            "@/modules/PhishTitan/views/Customer/Incidents/IncidentSummary/AffectedUsers/index.vue"
          ),
      },
      {
        path: "received-headers",
        name: "customer-email_security-incidents-incident-summary-received-headers",
        component: () =>
          import(
            "@/modules/PhishTitan/views/Customer/Incidents/IncidentSummary/ReceivedHeaders/index.vue"
          ),
      },
      {
        path: "timeline",
        name: "customer-email_security-incidents-incident-summary-incident-timeline",
        redirect: {
          name: "customer-email_security-incidents-incident-summary-details",
        },
      },
      {
        path: "audit-logs",
        name: "customer-email_security-incidents-incident-summary-audit-logs",
        redirect: {
          name: "customer-email_security-incidents-incident-summary-details",
        },
      },
      {
        path: "notes",
        name: "customer-email_security-incidents-incident-summary-audit-notes",
        redirect: {
          name: "customer-email_security-incidents-incident-summary-details",
        },
      },
    ],
  },
  {
    path: "domains",
    name: "customer-email_security-domains",
    meta: {
      solutionId: SEG_SERVICE,
    },
    component: () =>
      import("@/modules/PhishTitan/views/Customer/Domains/Domains.vue"),
  },
  {
    path: "email-history",
    name: "customer-email_security-email-history",
    component: () =>
      import("@/modules/PhishTitan/views/Common/EmailHistory/EmailHistory.vue"),
  },
  {
    path: "quarantine",
    name: "customer-email_security-quarantine",
    meta: {
      solutionId: SEG_SERVICE,
    },
    component: () =>
      import("@/modules/PhishTitan/views/Common/Quarantine/Quarantine.vue"),
  },
];

export const customerEmailSecurityRoutes = [
  {
    path: "email_security",
    name: "customer-email_security",
    component: () => import("@/modules/PhishTitan/views/Customer"),
    meta: {
      solutionId: PHISH_SERVICE,
    },
    redirect: {
      name: "customer-email_security-overview",
    },
    children,
  },
];

export const customerRoutes = {
  path: "",
  component: () => import("@/layouts/Customer/CustomerLayout.vue"),
  children: customerEmailSecurityRoutes,
};
